import React from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  FormHelperText,
  FormControl,
  FormLabel,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { nikshayAPI } from "../../../../../services/nikshay.api";

// ---------------------------------------------------------------------------
// Option lists
// ---------------------------------------------------------------------------
const SITE_OF_DISEASE_OPTIONS = ["Pulmonary", "Extra Pulmonary"];

const EXTRA_PULMONARY_SITE_OPTIONS = [
  "Abdominal",
  "Bone (Excluding Spine)",
  "Genitourinary",
  "Lymph Node",
  "Miliary",
  "Other",
  "Pericardial",
  "Pleural",
  "Spinal",
  "TBM",
];

const BASIS_OF_DIAGNOSIS_DSTB = [
  "Microscopy ZN",
  "Microscopy Fluorescent",
  "CBNAAT",
  "Trunat (MTB)",
  "Trunat (MTB-RIF)",
  "Truenat (MTB)",
  "Truenat (MTB-RIF)",
  "Culture",
  "FLLPA",
  "SLLPA",
  "DST",
  "Chest X Ray",
  "TST",
  "IGRA",
  "Cy-Tb",
  "Cytopathology",
  "Histopathology",
  "Gene Sequencing",
  "Other",
  "Microscopy ZN and Fluorescent",
  "F Line LPA",
  "S Line LPA",
  "Pathodetect - MTB, Rif & Inh",
  "Quantiplus-MTB",
];

const BASIS_OF_DIAGNOSIS_PMDT = [
  "Microscopy ZN",
  "Microscopy Fluorescent",
  "CBNAAT",
  "Trunat (MTB)",
  "Trunat (MTB-RIF)",
  "Truenat (MTB)",
  "Truenat (MTB-RIF)",
  "Culture",
  "FLLPA",
  "SLLPA",
  "DST",
  "Chest X Ray",
  "TST",
  "IGRA",
  "Cy-Tb",
  "Cytopathology",
  "Histopathology",
  "Gene Sequencing",
  "Other",
  "Microscopy ZN and Fluorescent",
  "F Line LPA",
  "S Line LPA",
  "Pathodetect - MTB, Rif & Inh",
  "CBNAAT-MTB/XDR",
];

const TREATMENT_PHASE_OPTIONS = ["IP", "CP"];

// ---------------------------------------------------------------------------
// Yup validation schema
// ---------------------------------------------------------------------------
const validationSchema = Yup.object().shape({
  TypeOfTreatment: Yup.string().required("Type of Treatment is required"),
  InitialWeight: Yup.number()
    .typeError("Must be a number")
    .required("Initial Weight is required")
    .min(1, "Must be greater than 0")
    .max(999, "Max 3 digits allowed"),
  DateOfDiagnosis: Yup.string().when("TypeOfTreatment", {
    is: "DSTB",
    then: (s) => s.required("Date of Diagnosis is required"),
    otherwise: (s) => s.nullable(),
  }),
  SiteOfDisease: Yup.string().when("TypeOfTreatment", {
    is: (val) => val !== "TPT (TB Prevention Therapy)",
    then: (s) => s.required("Site of Disease is required"),
    otherwise: (s) => s.nullable(),
  }),
  ExtraPulmonarySite: Yup.string().when(["TypeOfTreatment", "SiteOfDisease"], {
    is: (type, site) =>
      type !== "TPT (TB Prevention Therapy)" && site === "Extra Pulmonary",
    then: (s) => s.required("Extra Pulmonary Site is required"),
    otherwise: (s) => s.nullable(),
  }),
  BasisOfDiagnosis: Yup.string().when("TypeOfTreatment", {
    is: (val) => val !== "TPT (TB Prevention Therapy)",
    then: (s) => s.required("Basis of Diagnosis is required"),
    otherwise: (s) => s.nullable(),
  }),
  Height: Yup.number()
    .typeError("Must be a number")
    .required("Height is required")
    .min(1, "Must be greater than 0"),
  DateOfTBTreatmentInitiation: Yup.string().when("TypeOfTreatment", {
    is: (val) => val !== "TPT (TB Prevention Therapy)",
    then: (s) => s.required("Date of TB Initiation is required"),
    otherwise: (s) => s.nullable(),
  }),
  DateOfTPTInitiation: Yup.string().when("TypeOfTreatment", {
    is: "TPT (TB Prevention Therapy)",
    then: (s) => s.required("Date of TPT Initiation is required"),
    otherwise: (s) => s.nullable(),
  }),
  TreatmentPhase: Yup.string().when("TypeOfTreatment", {
    is: (val) => val !== "TPT (TB Prevention Therapy)",
    then: (s) => s.required("Treatment Phase is required"),
    otherwise: (s) => s.nullable(),
  }),
  EndOfIP: Yup.string().nullable(),
  Regimen: Yup.string().required("Regimen is required"),
  AdherenceTechnology: Yup.string().required("Adherence Technology is required"),
  AdherenceDispensationCorrelation: Yup.string().nullable(),
  TypeOfDot: Yup.string().nullable(),
});

// ---------------------------------------------------------------------------
// Initial values
// ---------------------------------------------------------------------------
const INITIAL_VALUES = {
  TypeOfTreatment: "DSTB",
  InitialWeight: "",
  DateOfDiagnosis: "",
  SiteOfDisease: "",
  ExtraPulmonarySite: "",
  BasisOfDiagnosis: "",
  Height: "",
  DateOfTBTreatmentInitiation: "",
  DateOfTPTInitiation: "",
  TreatmentPhase: "",
  EndOfIP: "",
  Regimen: "",
  AdherenceTechnology: "",
  AdherenceDispensationCorrelation: "True",
  TypeOfDot: "",
};

const SectionTitle = ({ title }) => (
  <Box
    sx={{
      background: "#f5f5f5",
      px: 2,
      py: 1,
      borderBottom: "1px solid #ddd",
    }}
  >
    <Typography fontWeight={600}>{title}</Typography>
  </Box>
);

const RegimenSelect = ({ typeOfTreatment, name, value, onChange, error, helperText, isTPT, isPMDT }) => {
  const [regimenOptions, setRegimenOptions] = React.useState([]);

  React.useEffect(() => {
    const fetchRegimens = async () => {
      if (!typeOfTreatment) {
        setRegimenOptions([]);
        return;
      }
      try {
        const res = await nikshayAPI.getRegimenV2(typeOfTreatment);
        if (res?.Success && Array.isArray(res?.Data)) {
          setRegimenOptions(res.Data);
        } else {
          setRegimenOptions([]);
        }
      } catch (err) {
        console.error("Error fetching regimen options:", err);
        setRegimenOptions([]);
      }
    };

    fetchRegimens();
  }, [typeOfTreatment]);

  return (
    <TextField
      select
      fullWidth
      size="small"
      required
      label={isTPT ? "TPT Regimen" : isPMDT ? "DRTB Regimen" : "DSTB Regimen"}
      name={name}
      value={value}
      onChange={onChange}
      error={error}
      helperText={helperText}
    >
      {regimenOptions.map((opt) => (
        <MenuItem key={opt.Key} value={opt.Key}>
          {opt.Value}
        </MenuItem>
      ))}
    </TextField>
  );
};

const StartTreatmentForm = ({ patientId: patientIdProp, episodeId: episodeIdProp, onCancel, onSaveSuccess }) => {
  const patientId = patientIdProp || localStorage.getItem("nikshay_patient_id");
  const today = new Date().toISOString().split("T")[0];

  const [formExistingData, setFormExistingData] = React.useState(null);
  const [loadingFormData, setLoadingFormData] = React.useState(true);
  const [typeOfTreatmentOptions, setTypeOfTreatmentOptions] = React.useState([]);
  const [adherenceTechnologyOptions, setAdherenceTechnologyOptions] = React.useState([]);

  const activeEpisodeId = episodeIdProp || patientId;

  React.useEffect(() => {
    const fetchFormExistingData = async () => {
      if (!activeEpisodeId) {
        setLoadingFormData(false);
        return;
      }
      try {
        setLoadingFormData(true);
        const res = await nikshayAPI.getFormExistingData(activeEpisodeId);
        if (res?.success && res?.ExistingData) {
          setFormExistingData(res.ExistingData);
        } else if (res?.ExistingData) {
          setFormExistingData(res.ExistingData);
        } else {
          setFormExistingData(null);
        }
      } catch (err) {
        console.error("Error fetching form existing data:", err);
        setFormExistingData(null);
      } finally {
        setLoadingFormData(false);
      }
    };

    fetchFormExistingData();
  }, [activeEpisodeId]);

  const effectiveData = formExistingData;

  React.useEffect(() => {
    const fetchOptions = async () => {
      if (!activeEpisodeId) return;

      try {
        const typeRes = await nikshayAPI.getTypeOfTreatment(activeEpisodeId);
        if (typeRes?.Success && Array.isArray(typeRes?.Data)) {
          setTypeOfTreatmentOptions(typeRes.Data);
        }
      } catch (err) {
        console.error("Error fetching Type of Treatment options:", err);
      }

      const hierarchyId = effectiveData?.HierarchyMapCurrent;
      if (hierarchyId) {
        try {
          const adherenceRes = await nikshayAPI.getAdherenceTechnology(hierarchyId);
          if (adherenceRes?.Success && Array.isArray(adherenceRes?.Data)) {
            setAdherenceTechnologyOptions(adherenceRes.Data);
          }
        } catch (err) {
          console.error("Error fetching Adherence Technology options:", err);
        }
      }
    };

    fetchOptions();
  }, [activeEpisodeId, effectiveData]);

  const displayTreatmentOptions = typeOfTreatmentOptions;
  const displayAdherenceOptions = adherenceTechnologyOptions;

  const getVal = (field) => {
    if (!field) return "";
    if (typeof field === "object") {
      return field.Key || field.Value || "";
    }
    return field;
  };

  const formatDateStr = (dateStr) => {
    if (!dateStr) return "";
    if (typeof dateStr === "string" && /^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
      const [d, m, y] = dateStr.split("-");
      return `${y}-${m}-${d}`;
    }
    return dateStr;
  };

  const getInitialValues = () => {
    if (!effectiveData) return INITIAL_VALUES;

    let typeOfTreatment = getVal(effectiveData.TypeOfTreatment);
    if (!typeOfTreatment) {
      if (effectiveData.DateOfDRTBDiagnosis || effectiveData.BasisOfDRTBDiagnosis || effectiveData.HasDRTBTest === "True" || effectiveData.DRTBRegimen) {
        typeOfTreatment = "PMDT";
      } else if (effectiveData.DateOfLTBIDiagnosis || effectiveData.BasisOfLTBIDiagnosis || effectiveData.HasLTBITest === "True" || effectiveData.TPTRegimen) {
        typeOfTreatment = "TPT (TB Prevention Therapy)";
      } else if (effectiveData.DateOfDSTBDiagnosis || effectiveData.BasisOfDSTBDiagnosis || effectiveData.HasDSTBTest === "True" || effectiveData.DSTBRegimen) {
        typeOfTreatment = "DSTB";
      } else if (typeOfTreatmentOptions.length > 0) {
        typeOfTreatment = typeOfTreatmentOptions[0].Key;
      } else {
        typeOfTreatment = "DSTB";
      }
    }

    let dateOfDiagnosis = "";
    let basisOfDiagnosis = "";
    let regimen = "";

    if (typeOfTreatment === "DSTB") {
      dateOfDiagnosis = formatDateStr(effectiveData.DateOfDSTBDiagnosis) || "";
      basisOfDiagnosis = getVal(effectiveData.BasisOfDSTBDiagnosis) || "";
      regimen = getVal(effectiveData.DSTBRegimen) || "";
    } else if (typeOfTreatment === "PMDT") {
      dateOfDiagnosis = formatDateStr(effectiveData.DateOfDRTBDiagnosis) || "";
      basisOfDiagnosis = getVal(effectiveData.BasisOfDRTBDiagnosis) || "";
      regimen = getVal(effectiveData.DRTBRegimen) || "";
    } else if (typeOfTreatment === "TPT (TB Prevention Therapy)") {
      dateOfDiagnosis = formatDateStr(effectiveData.DateOfLTBIDiagnosis) || "";
      basisOfDiagnosis = getVal(effectiveData.BasisOfLTBIDiagnosis) || "";
      regimen = getVal(effectiveData.TPTRegimen) || "";
    }

    return {
      TypeOfTreatment: typeOfTreatment,
      InitialWeight: effectiveData.InitialWeight || "",
      DateOfDiagnosis: dateOfDiagnosis || "",
      SiteOfDisease: effectiveData.SiteOfDisease || "",
      ExtraPulmonarySite: effectiveData.ExtraPulmonarySite || "",
      BasisOfDiagnosis: basisOfDiagnosis,
      Height: effectiveData.Height || "",
      DateOfTBTreatmentInitiation: formatDateStr(effectiveData.DateOfTBTreatmentInitiation) || "",
      DateOfTPTInitiation: formatDateStr(effectiveData.DateofTPTInitiation) || "",
      TreatmentPhase: effectiveData.TreatmentPhase || "",
      EndOfIP: formatDateStr(effectiveData.EndOfIP) || "",
      Regimen: regimen || "",
      AdherenceTechnology: effectiveData.AdherenceTechnology?.Key || getVal(effectiveData.AdherenceTechnology) || "",
      AdherenceDispensationCorrelation: effectiveData.AdherenceDispensationCorrelation !== undefined && effectiveData.AdherenceDispensationCorrelation !== null
        ? String(effectiveData.AdherenceDispensationCorrelation)
        : "True",
      TypeOfDot: effectiveData.TypeOfDot || "",
    };
  };

  if (loadingFormData) {
    return (
      <Box p={3} display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2, color: "text.secondary" }}>
          Loading form details...
        </Typography>
      </Box>
    );
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const activeEpisodeId = episodeIdProp || patientId;
    if (!activeEpisodeId) {
      toast.error("Episode ID is missing. Cannot save treatment details.");
      setSubmitting(false);
      return;
    }
    try {
      const payload = {
        EpisodeId: String(activeEpisodeId),
        TypeOfTreatment: (() => {
          const found = displayTreatmentOptions.find(
            (opt) => (opt.Value === values.TypeOfTreatment || opt.Key === values.TypeOfTreatment)
          );
          if (found && typeof found === "object") {
            return { Value: found.Value, Key: found.Key  };
          }
          return { Value: values.TypeOfTreatment, Key: values.TypeOfTreatment };
        })(),
        HierarchyMapCurrent: effectiveData?.HierarchyMapCurrent || null,
        InitialWeight: values.InitialWeight ? String(values.InitialWeight) : null,
        SiteOfDisease: values.TypeOfTreatment !== "TPT (TB Prevention Therapy)" ? values.SiteOfDisease || null : null,
        ExtraPulmonarySite: (values.TypeOfTreatment !== "TPT (TB Prevention Therapy)" && values.SiteOfDisease === "Extra Pulmonary") ? values.ExtraPulmonarySite || null : null,
        Height: values.Height ? String(values.Height) : null,
        DateOfTBTreatmentInitiation: values.TypeOfTreatment !== "TPT (TB Prevention Therapy)"
          ? (values.DateOfTBTreatmentInitiation || formatDateStr(effectiveData?.DateOfTBTreatmentInitiation) || null)
          : null,
        TreatmentPhase: values.TypeOfTreatment !== "TPT (TB Prevention Therapy)" ? values.TreatmentPhase || null : null,
        EndOfIP: (values.TypeOfTreatment !== "TPT (TB Prevention Therapy)" && values.TreatmentPhase === "CP") ? values.EndOfIP || null : null,
        DateofTPTInitiation: values.TypeOfTreatment === "TPT (TB Prevention Therapy)"
          ? (values.DateOfTPTInitiation || formatDateStr(effectiveData?.DateofTPTInitiation) || null)
          : null,
        HasDSTBTest: effectiveData?.HasDSTBTest ? "True" : null,
        DateOfDSTBDiagnosis: (values.TypeOfTreatment === "DSTB" && values.DateOfDiagnosis) || formatDateStr(effectiveData?.DateOfDSTBDiagnosis) || null,
        BasisOfDSTBDiagnosis: (values.TypeOfTreatment === "DSTB" && values.BasisOfDiagnosis) || getVal(effectiveData?.BasisOfDSTBDiagnosis) || null,
        HasDRTBTest: effectiveData?.HasDRTBTest ? "True" : null,
        DateOfDRTBDiagnosis: (values.TypeOfTreatment === "PMDT" && values.DateOfDiagnosis) || formatDateStr(effectiveData?.DateOfDRTBDiagnosis) || null,
        BasisOfDRTBDiagnosis: (values.TypeOfTreatment === "PMDT" && values.BasisOfDiagnosis) || getVal(effectiveData?.BasisOfDRTBDiagnosis) || null,
        HasLTBITest: effectiveData?.HasLTBITest ? "True" : null,
        DateOfLTBIDiagnosis: (values.TypeOfTreatment === "TPT (TB Prevention Therapy)" && values.DateOfDiagnosis) || formatDateStr(effectiveData?.DateOfLTBIDiagnosis) || null,
        BasisOfLTBIDiagnosis: (values.TypeOfTreatment === "TPT (TB Prevention Therapy)" && values.BasisOfDiagnosis) || getVal(effectiveData?.BasisOfLTBIDiagnosis) || null,
        DSTBNotificationTestId: null,
        DRTBNotificationTestId: null,
        LTBINotificationTestId: null,
        AdherenceTechnology: (() => {
          if (!values.AdherenceTechnology) return null;
          const found = displayAdherenceOptions.find(
            (opt) => (opt.Value === values.AdherenceTechnology || opt.Key === values.AdherenceTechnology)
          );
          if (found && typeof found === "object") {
            return { Key: found.Key, Value: found.Value };
          }
          return { Key: values.AdherenceTechnology, Value: values.AdherenceTechnology };
        })(),
        MermSerialNoObj: null,
        AdherenceDispensationCorrelation: values.AdherenceDispensationCorrelation || null,
        TypeOfDot: values.TypeOfDot || null,
        LastSeen: null,
        BatteryLevel: null,
        DSTBRegimen: values.TypeOfTreatment === "DSTB" && values.Regimen ? { Key: values.Regimen, Value: values.Regimen } : null,
        DRTBRegimen: values.TypeOfTreatment === "PMDT" && values.Regimen ? { Key: values.Regimen, Value: values.Regimen } : null,
        TPTRegimen: values.TypeOfTreatment === "TPT (TB Prevention Therapy)" && values.Regimen ? { Key: values.Regimen, Value: values.Regimen } : null,
      };

      await nikshayAPI.startTreatment(activeEpisodeId, payload);
      toast.success("Treatment details saved successfully!");
      if (onSaveSuccess) {
        onSaveSuccess();
      }
    } catch (error) {
      console.error("Treatment save error:", error);
      toast.error(
        error?.response?.data?.message || "Failed to save treatment details."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, isSubmitting, handleChange, setFieldValue }) => {
        const isTPT = values.TypeOfTreatment === "TPT (TB Prevention Therapy)";
        const isPMDT = values.TypeOfTreatment === "PMDT";
        const isDSTB = values.TypeOfTreatment === "DSTB";
        const isExtraPulmonary = values.SiteOfDisease === "Extra Pulmonary";

        const diagKey = isDSTB ? "DSTB" : isPMDT ? "DRTB" : isTPT ? "LTBI" : null;
        const isDateOfDiagnosisFrozen = Boolean(diagKey && effectiveData?.[`DateOf${diagKey}Diagnosis`]);
        const isBasisOfDiagnosisFrozen = Boolean(diagKey && effectiveData?.[`BasisOf${diagKey}Diagnosis`]);

        const basisOptions = isPMDT
          ? BASIS_OF_DIAGNOSIS_PMDT
          : BASIS_OF_DIAGNOSIS_DSTB;

        return (
          <Form>
            <Box p={3} sx={{ "& .MuiFormLabel-asterisk": { color: "error.main" } }}>
              {/* ---- Header ---- */}
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="h5" fontWeight={600}>
                    Start Treatment
                  </Typography>
                </Box>
                {onCancel && (
                  <Button variant="outlined" color="inherit" onClick={onCancel}>
                    Cancel
                  </Button>
                )}
              </Box>

              {/* ---- Episode Details ---- */}
              <Paper sx={{ mb: 3 }}>
                <SectionTitle title="Episode Details" />
                <Box p={3}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, lg: 4 }}>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        required
                        label="Type Of Treatment"
                        name="TypeOfTreatment"
                        value={values.TypeOfTreatment}
                        error={touched.TypeOfTreatment && Boolean(errors.TypeOfTreatment)}
                        helperText={touched.TypeOfTreatment && errors.TypeOfTreatment}
                        onChange={(e) => {
                          const newType = e.target.value;
                          setFieldValue("TypeOfTreatment", newType);
                          setFieldValue("Regimen", "");
                          setFieldValue("ExtraPulmonarySite", "");
                          setFieldValue("DateOfTPTInitiation", formatDateStr(effectiveData?.DateofTPTInitiation || effectiveData?.DateOfTPTInitiation) || "");
                          setFieldValue("DateOfTBTreatmentInitiation", formatDateStr(effectiveData?.DateOfTBTreatmentInitiation || effectiveData?.DateofTBTreatmentInitiation) || "");
                          setFieldValue("TreatmentPhase", "");
                          setFieldValue("EndOfIP", "");

                          if (newType === "DSTB") {
                            setFieldValue("DateOfDiagnosis", formatDateStr(effectiveData?.DateOfDSTBDiagnosis) || "");
                            setFieldValue("BasisOfDiagnosis", getVal(effectiveData?.BasisOfDSTBDiagnosis) || "");
                          } else if (newType === "PMDT") {
                            setFieldValue("DateOfDiagnosis", formatDateStr(effectiveData?.DateOfDRTBDiagnosis) || "");
                            setFieldValue("BasisOfDiagnosis", getVal(effectiveData?.BasisOfDRTBDiagnosis) || "");
                          } else if (newType === "TPT (TB Prevention Therapy)") {
                            setFieldValue("DateOfDiagnosis", formatDateStr(effectiveData?.DateOfLTBIDiagnosis) || "");
                            setFieldValue("BasisOfDiagnosis", getVal(effectiveData?.BasisOfLTBIDiagnosis) || "");
                          } else {
                            setFieldValue("DateOfDiagnosis", "");
                            setFieldValue("BasisOfDiagnosis", "");
                          }
                        }}
                      >
                        {displayTreatmentOptions.map((opt) => (
                          <MenuItem key={opt.Key} value={opt.Key}>
                            {opt.Value}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>

              {/* ---- Diagnosis ---- */}
              <Paper sx={{ mb: 3 }}>
                <SectionTitle title="Diagnosis" />
                <Box p={3}>
                  <Grid container spacing={3}>
                    {/* Initial Weight */}
                    <Grid size={{ xs: 12, lg: 4 }}>
                      <TextField
                        fullWidth
                        size="small"
                        required
                        label="Initial Weight"
                        name="InitialWeight"
                        type="number"
                        inputProps={{ min: 1, max: 999 }}
                        value={values.InitialWeight}
                        onChange={handleChange}
                        error={touched.InitialWeight && Boolean(errors.InitialWeight)}
                        helperText={touched.InitialWeight && errors.InitialWeight}
                      />
                    </Grid>

                    {isTPT ? (
                      <>
                        {/* Height */}
                        <Grid size={{ xs: 12, lg: 4 }}>
                          <TextField
                            fullWidth
                            size="small"
                            required
                            label="Height (in cms)"
                            name="Height"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={values.Height}
                            onChange={handleChange}
                            error={touched.Height && Boolean(errors.Height)}
                            helperText={touched.Height && errors.Height}
                          />
                        </Grid>

                        {/* Date of TPT Initiation */}
                        <Grid size={{ xs: 12, lg: 4 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              views={["year", "month", "day"]}
                              label="Date of TPT Initiation"
                              format="DD-MM-YYYY"
                              maxDate={dayjs()}
                              value={values.DateOfTPTInitiation ? dayjs(values.DateOfTPTInitiation) : null}
                              onChange={(val) => {
                                setFieldValue(
                                  "DateOfTPTInitiation",
                                  val && val.isValid() ? val.format("YYYY-MM-DD") : ""
                                );
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  required: true,
                                  error: touched.DateOfTPTInitiation && Boolean(errors.DateOfTPTInitiation),
                                  helperText: touched.DateOfTPTInitiation && errors.DateOfTPTInitiation,
                                  name: "DateOfTPTInitiation",
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>
                      </>
                    ) : (
                      <>
                        {/* Date of Diagnosis */}
                        <Grid size={{ xs: 12, lg: 4 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              views={["year", "month", "day"]}
                              label="Date of Diagnosis"
                              format="DD-MM-YYYY"
                              maxDate={dayjs()}
                              disabled={isDateOfDiagnosisFrozen}
                              value={values.DateOfDiagnosis ? dayjs(values.DateOfDiagnosis) : null}
                              onChange={(val) => {
                                setFieldValue(
                                  "DateOfDiagnosis",
                                  val && val.isValid() ? val.format("YYYY-MM-DD") : ""
                                );
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  required: true,
                                  disabled: isDateOfDiagnosisFrozen,
                                  error: touched.DateOfDiagnosis && Boolean(errors.DateOfDiagnosis),
                                  helperText: touched.DateOfDiagnosis && errors.DateOfDiagnosis,
                                  name: "DateOfDiagnosis",
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>

                        {/* Site of Disease */}
                        <Grid size={{ xs: 12, lg: 4 }}>
                          <FormControl required error={touched.SiteOfDisease && Boolean(errors.SiteOfDisease)}>
                            <FormLabel sx={{ fontSize: 14 }}>Site of Disease</FormLabel>
                            <RadioGroup
                              name="SiteOfDisease"
                              value={values.SiteOfDisease}
                              sx={{ flexDirection: { xs: "column", md: "row" }, gap: 2 }}
                              onChange={(e) => {
                                setFieldValue("SiteOfDisease", e.target.value);
                                if (e.target.value !== "Extra Pulmonary") {
                                  setFieldValue("ExtraPulmonarySite", "");
                                }
                              }}
                            >
                              {SITE_OF_DISEASE_OPTIONS.map((opt) => (
                                <FormControlLabel
                                  key={opt}
                                  value={opt}
                                  control={<Radio size="small" />}
                                  label={opt}
                                />
                              ))}
                            </RadioGroup>
                            {touched.SiteOfDisease && errors.SiteOfDisease && (
                              <FormHelperText>{errors.SiteOfDisease}</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>

                        {/* Extra Pulmonary Site */}
                        {isExtraPulmonary && (
                          <Grid size={{ xs: 12, lg: 4 }}>
                            <TextField
                              fullWidth
                              select
                              size="small"
                              required
                              label="Extra Pulmonary Site"
                              name="ExtraPulmonarySite"
                              value={values.ExtraPulmonarySite}
                              onChange={handleChange}
                              error={touched.ExtraPulmonarySite && Boolean(errors.ExtraPulmonarySite)}
                              helperText={touched.ExtraPulmonarySite && errors.ExtraPulmonarySite}
                            >
                              {EXTRA_PULMONARY_SITE_OPTIONS.map((opt) => (
                                <MenuItem key={opt} value={opt}>
                                  {opt}
                                </MenuItem>
                              ))}
                            </TextField>
                          </Grid>
                        )}

                        {/* Basis of Diagnosis */}
                        <Grid size={{ xs: 12, lg: 4 }}>
                          <TextField
                            fullWidth
                            select
                            size="small"
                            required
                            disabled={isBasisOfDiagnosisFrozen}
                            label="Basis of Diagnosis"
                            name="BasisOfDiagnosis"
                            value={values.BasisOfDiagnosis}
                            onChange={handleChange}
                            error={touched.BasisOfDiagnosis && Boolean(errors.BasisOfDiagnosis)}
                            helperText={touched.BasisOfDiagnosis && errors.BasisOfDiagnosis}
                          >
                            {basisOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        {/* Height */}
                        <Grid size={{ xs: 12, lg: 4 }}>
                          <TextField
                            fullWidth
                            size="small"
                            required
                            label="Height (in cms)"
                            name="Height"
                            type="number"
                            inputProps={{ min: 1 }}
                            value={values.Height}
                            onChange={handleChange}
                            error={touched.Height && Boolean(errors.Height)}
                            helperText={touched.Height && errors.Height}
                          />
                        </Grid>

                        {/* Date of TB Treatment Initiation */}
                        <Grid size={{ xs: 12, lg: 4 }}>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              views={["year", "month", "day"]}
                              label="Date of TB Initiation"
                              format="DD-MM-YYYY"
                              maxDate={dayjs()}
                              value={values.DateOfTBTreatmentInitiation ? dayjs(values.DateOfTBTreatmentInitiation) : null}
                              onChange={(val) => {
                                setFieldValue(
                                  "DateOfTBTreatmentInitiation",
                                  val && val.isValid() ? val.format("YYYY-MM-DD") : ""
                                );
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  required: true,
                                  error: touched.DateOfTBTreatmentInitiation && Boolean(errors.DateOfTBTreatmentInitiation),
                                  helperText: touched.DateOfTBTreatmentInitiation && errors.DateOfTBTreatmentInitiation,
                                  name: "DateOfTBTreatmentInitiation",
                                },
                              }}
                            />
                          </LocalizationProvider>
                        </Grid>

                        {/* Treatment Phase */}
                        <Grid size={{ xs: 12, lg: 4 }}>
                          <TextField
                            fullWidth
                            select
                            size="small"
                            required
                            label="Treatment Phase"
                            name="TreatmentPhase"
                            value={values.TreatmentPhase}
                            onChange={(e) => {
                              setFieldValue("TreatmentPhase", e.target.value);
                              if (e.target.value !== "CP") {
                                setFieldValue("EndOfIP", "");
                              }
                            }}
                            error={touched.TreatmentPhase && Boolean(errors.TreatmentPhase)}
                            helperText={touched.TreatmentPhase && errors.TreatmentPhase}
                          >
                            {TREATMENT_PHASE_OPTIONS.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Grid>

                        {/* End of IP */}
                        {values.TreatmentPhase === "CP" && (
                          <Grid size={{ xs: 12, lg: 4 }}>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                views={["year", "month", "day"]}
                                label="End of IP"
                                format="DD-MM-YYYY"
                                value={values.EndOfIP ? dayjs(values.EndOfIP) : null}
                                onChange={(val) => {
                                  setFieldValue(
                                    "EndOfIP",
                                    val && val.isValid() ? val.format("YYYY-MM-DD") : ""
                                  );
                                }}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    size: "small",
                                    required: false,
                                    error: touched.EndOfIP && Boolean(errors.EndOfIP),
                                    helperText: touched.EndOfIP && errors.EndOfIP,
                                    name: "EndOfIP",
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </Grid>
                        )}
                      </>
                    )}
                  </Grid>
                </Box>
              </Paper>

              {/* ---- Regimen ---- */}
              <Paper sx={{ mb: 3 }}>
                <SectionTitle title="Regimen" />
                <Box p={3}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, lg: 4 }}>
                      <RegimenSelect
                        typeOfTreatment={values.TypeOfTreatment}
                        name="Regimen"
                        value={values.Regimen}
                        onChange={handleChange}
                        error={touched.Regimen && Boolean(errors.Regimen)}
                        helperText={touched.Regimen && errors.Regimen}
                        isTPT={isTPT}
                        isPMDT={isPMDT}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>

              {/* ---- Adherence Monitoring ---- */}
              <Paper sx={{ mb: 3 }}>
                <SectionTitle title="Adherence Monitoring" />
                <Box p={3}>
                  <Grid container spacing={3}>
                    {/* Adherence Technology */}
                    <Grid size={{ xs: 12, lg: 4 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        required
                        label="Adherence Technology"
                        name="AdherenceTechnology"
                        value={values.AdherenceTechnology}
                        onChange={handleChange}
                        error={touched.AdherenceTechnology && Boolean(errors.AdherenceTechnology)}
                        helperText={touched.AdherenceTechnology && errors.AdherenceTechnology}
                      >
                        {displayAdherenceOptions.map((opt) => (
                          <MenuItem key={opt.Key} value={opt.Key}>
                            {opt.Value}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>

                    {/* Adherence and Dispensation Correlation */}
                    <Grid size={{ xs: 12, lg: 4 }}>
                      <FormControl error={touched.AdherenceDispensationCorrelation && Boolean(errors.AdherenceDispensationCorrelation)}>
                        <FormLabel sx={{ fontSize: 14 }}>Adherence and Dispensation Correlation</FormLabel>
                        <RadioGroup
                          row
                          name="AdherenceDispensationCorrelation"
                          value={values.AdherenceDispensationCorrelation}
                          onChange={handleChange}
                        >
                          <FormControlLabel value="True" control={<Radio size="small" />} label="Yes" />
                          <FormControlLabel value="False" control={<Radio size="small" />} label="No" />
                        </RadioGroup>
                        {touched.AdherenceDispensationCorrelation && errors.AdherenceDispensationCorrelation && (
                          <FormHelperText>{errors.AdherenceDispensationCorrelation}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    {/* Type of DOT */}
                    <Grid size={{ xs: 12, lg: 4 }}>
                      <FormControl error={touched.TypeOfDot && Boolean(errors.TypeOfDot)}>
                        <FormLabel sx={{ fontSize: 14 }}>Type of Dot</FormLabel>
                        <RadioGroup name="TypeOfDot" value={values.TypeOfDot} onChange={handleChange}>
                          <FormControlLabel value="Institutional Treatment Supporter" control={<Radio size="small" />} label="Institutional Treatment Supporter" />
                          <FormControlLabel value="Community Treatment Supporter" control={<Radio size="small" />} label="Community Treatment Supporter" />
                          <FormControlLabel value="Family DOT" control={<Radio size="small" />} label="Family DOT" />
                        </RadioGroup>
                        {touched.TypeOfDot && errors.TypeOfDot && (
                          <FormHelperText>{errors.TypeOfDot}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>

              {/* ---- Submit Row ---- */}
              <Box display="flex" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : <span>+</span>}
                  className="search-btn-abha"
                  sx={{ borderRadius: "25px", px: 3 }}
                >
                  {isSubmitting ? "Saving..." : "START TREATMENT"}
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default StartTreatmentForm;
